<template>
  <div>
      <Toast />
      <ConfirmDialog></ConfirmDialog>
      <Panel header="Account Settings" class="p-mt-3">
        <Divider align="left">
            <div class="inline-flex align-items-center">
                <b>Contact</b>
            </div>
        </Divider>
        <form class="settings-form" v-on:submit.prevent>
            <div class="p-field">
                <div class="p-float-label">
                    <InputText id="email" v-model="contact.email" />
                    <label for="email">Email</label>
                    <small class="p-error" v-if="errors.email">{{ errors.email }}</small>
                </div>
            </div>
            <div class="p-field">
                <div class="p-float-label">
                    <InputText id="phone" v-model="contact.phone" />
                    <label for="phone">Phone #</label>
                    <small class="p-error" v-if="errors.phone">{{ errors.phone }}</small>
                </div>
            </div>
            <div class="p-field-checkbox">
                  <Checkbox id="phone_allow_text" :binary="true" v-model="contact.allow_text"></Checkbox>
                  <label for="phone_allow_text">Allow text messages</label>
              </div>
            
            <Button label="Update" icon="pi pi-check-square" :loading="loading" @click="updateContact"></Button>
        </form>
        <Divider align="left">
            <div class="inline-flex align-items-center">
                <b>Notifications</b>
            </div>
        </Divider>
        <Divider align="left">
            <div class="inline-flex align-items-center">
                <b>Password</b>
            </div>
        </Divider>
        <div class="password-form">
          <form class="settings-form" autocomplete="off" v-on:submit.prevent>
              <div class="p-field">
                  <div class="p-float-label">
                      <Password id="new_password" v-model="new_password" :feedback="true" />
                      <label for="new_password">New password</label>
                      <br v-if="errors.new_password" />
                      <small class="p-error" v-if="errors.new_password">{{ errors.new_password }}</small>
                  </div>
              </div>
              <div class="p-field">
                  <div class="p-float-label">
                      <Password id="confirm_password" v-model="confirm_password" :feedback="false" />
                      <label for="confirm_password">Confirm password</label>
                      <small class="p-error" v-if="errors.confirm_password">{{ errors.confirm_password }}</small>
                  </div>
              </div>
              
              <Button label="Change password" icon="pi pi-lock" :loading="loading" @click="changePassword"></Button>
          </form>
        </div>
        <Divider align="left">
            <div class="inline-flex align-items-center">
                <b>Multi-factor Authentication</b>
            </div>
        </Divider>

        <Button v-if="!totp.enabled" label="Enroll" icon="pi pi-shield" @click="mfaEnrollRequest"></Button>
        <Button v-if="totp.enabled" label="Remove 2FA" icon="pi pi-lock-open" class="p-button-warning" @click="mfaRemove"></Button>
        <Divider align="left">
            <div class="inline-flex align-items-center">
                <b>API Keys</b>
            </div>
        </Divider>
        <Button label="Generate Key" icon="pi pi-bolt" @click="displayNewAPIKeyModal = true" class="p-mb-3"></Button>
        <DataTable v-if="APIKeys" :value="APIKeys.keys" class="p-datatable-sm" responsiveLayout="stack" @row-click="openAPIKeyModal($event.data.id)" rowHover stripedRows selectionMode="single">
            <template #empty>
              <div style="text-align: center;">No keys found</div>
            </template>
            <Column field="prefix" header="Prefix"></Column>
            <Column field="label" header="Label"></Column>
            <Column field="permissions" header="Permissions">
              <template #body="slotProps">
                <div v-for="value,key in slotProps.data.permissions">
                  <div v-if="key == 'domains'">
                    <!-- <span>{{ key }} - {{ value }}</span> -->
                    <div v-for="permissions,domain_id in value">
                        <div v-for="v2,k2 in permissions">
                          <div v-if="k2=='dns'">
                            <span v-if="v2.allow != 'none'">
                            DNS {{ permissions.name }}: <Tag :value="v2.allow" :severity="getPermColor(v2.allow)"></Tag>
                            </span>
                            <div v-for="record,record_id in v2.records">
                              <span v-if="record.allow != 'none'">
                                {{ record.name }} ({{ record.type }}): 
                                <Tag :value="record.allow" :severity="getPermColor(record.allow)"></Tag>
                              </span>
                            </div>
                          </div>
                        </div>
                    </div>
                  </div>
                  <div v-if="key == 'global'">
                    <div v-for="permissions, service in value">
                        <span v-if="service == 'dns' && permissions.allow && permissions.allow != 'none'">Global DNS: <Tag :value="permissions.allow" :severity="getPermColor(permissions.allow)"></Tag></span>

                    </div>
                  </div>
                </div>
              </template>
            </Column>
            <Column header="Active" headerStyle="width: 100px; text-align: right">
              <template #body="slotProps">
                <i v-if="slotProps.data.is_enabled" style="color:var(--green-400)" class="pi pi-check-circle"></i>
                <i v-else style="color:var(--orange-400)" class="pi pi-minus-circle"></i>
              </template>
          </Column>
        </DataTable>
      </Panel>
  </div>
  <Dialog header="2FA Enrollment" v-model:visible="display2FASuccess" :modal="true">
    <div class="p-d-flex p-ai-center p-dir-col p-pt-3 p-px-3">
        <p>Please scan this QR code with your TOTP app (eg. Google Authenticator, Twilio's Authy).</p>
        <p><h4>This will be the only time this code is shown.</h4></p>
        <p :style="{lineHeight: 1.5, textIndent: '1rem'}">
          <Image :src="`data:image/png;base64,${qr_image}`" alt="Image Text">
            <template #indicator>
              Preview Content
            </template>
          </Image>      
        </p>
        <p class="p-error" v-if="errors.totp">{{ errors.totp }}</p>
    </div>
      
    <template #footer>
        <Button label="Done" @click="display2FASuccess = false"></Button>
    </template>
  </Dialog>
  <Dialog header="New API Key" v-model:visible="displayNewAPIKeyModal" :modal="true">
      <div class="p-pt-3">
          <form class="p-fluid" v-on:submit.prevent>
              <div class="p-field">
                  <div class="p-float-label">
                      <InputText id="label" v-model="api_key.label" v-on:keyup.enter="generateAPIKey"/>
                      <label for="label">Label</label>
                  </div>
              </div>
              <div class="p-field-checkbox">
                  <Checkbox id="active" :binary="true" v-model="api_key.is_enabled"></Checkbox>
                  <label for="active">Active</label>
              </div>              
          </form>
      </div>
      <template #footer>
        <Button label="Create" icon="pi pi-check-square" class="w-full" :loading="loading" @click="generateAPIKey"></Button>
      </template>
  </Dialog>
  <Dialog header="API Key" v-model:visible="displayAPIKeyModal"  :modal="true" @hide="onCloseAPIKeyModal">
      <div class="p-pt-3">
        <div v-if="api_key.key" class="p-pt-3 p-pb-5">
          <p>Copy and store this key in a safe place. This will be the only time it's displayed.</p>
          <div class="p-grid">
            <div class="p-col p-pt-3"><h3>{{ api_key.key }}</h3></div>
            <div class="p-col"><Button icon="pi pi-copy" class="p-button-rounded p-button-text" @click="copyAPIKey"  v-tooltip.bottom="'Copy'"/></div>            
          </div>
        </div>
        <form class="p-fluid" v-on:submit.prevent>
            <div class="p-field">
                <div class="p-float-label">
                    <InputText id="label" v-model="api_key.label" />
                    <label for="label">Label</label>
                </div>
            </div>
            <div class="p-field-checkbox">
                <Checkbox id="active" :binary="true" v-model="api_key.is_enabled"></Checkbox>
                <label for="active">Active</label>
            </div>
            <h4>Global DNS Permissions</h4>
            <SelectButton v-model="api_key.permissions.global.dns.allow" :options="readAllPermissions" optionLabel="name" optionValue="value"/>
            <h4 v-if="api_key.permissions.domains">Domain DNS Permissions</h4>
            <div v-if="api_key.permissions.domains" v-for="domain, domain_id in api_key.permissions.domains">
              <div class="p-grid">
                <div class="p-col-10 p-pt-4"><h5>{{ domain.name }}</h5></div>
                <div class="p-col">
                  <div align="right">
                    <Button icon="pi pi-times-circle" class="p-button-rounded p-button-text" @click="removeDomainFromAPIKey(domain_id)"  v-tooltip.bottom="'Remove'"/>
                  </div>
                </div>            
              </div>
              
              <SelectButton v-model="domain.dns.allow" :options="readAllPermissions" optionLabel="name" optionValue="value"/>
              <Divider />
              <div v-for="record, record_id in domain.dns.records">
                <div class="p-grid">
                  <div class="p-col-10 p-pt-4"><h6>{{ record.name}} ({{ record.type }})</h6></div>
                  <div class="p-col">
                    <div align="right">
                      <Button icon="pi pi-times-circle" class="p-button-rounded p-button-text" @click="removeRecordFromAPIKey(domain_id, record_id)"  v-tooltip.bottom="'Remove'"/>
                    </div>
                  </div>            
                </div>
                
                <SelectButton v-model="record.allow" :options="readAllPermissions" optionLabel="name" optionValue="value"/>
                <Divider />
              </div>
              <div class="col-12 md:col-4">
                <div class="p-inputgroup">
                    <Dropdown v-model="selectedRecord[domain_id]" :options="records_cache[domain_id]" placeholder="Select record">
                      <template #value="slotProps">
                          <div class="p-dropdown-value" v-if="slotProps.value">
                              <span>{{ slotProps.value.name }} ({{ slotProps.value.type }})</span>
                          </div>
                      </template>
                      <template #option="slotProps">
                        <span>{{ slotProps.option.name }} ({{ slotProps.option.type }})</span>
                    </template>
                    </Dropdown>
                    <Button icon="pi pi-plus" @click="addRecordToAPIKey(domain_id)" />
                </div>
              </div>  
            </div>

            <Divider />

            <div class="col-12 md:col-4">
              <h5>Add Domain Permissions</h5>
              <div class="p-inputgroup">
                  <Dropdown v-model="selectedDomain" :options="Domains.domains" optionLabel="name" placeholder="Select domain" />
                  <Button icon="pi pi-plus" @click="addDomainToAPIKey"/>
              </div>
            </div>

        </form>
      </div>
      <template #footer>
        <Button label="Delete" class="p-button-text p-button-danger" @click="removeAPIKey()"></Button>
        <Button label="Update" :loading="loading" @click="saveAPIKey"></Button>
      </template>
  </Dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import UserService from '../services/user.service';

export default {
  name: 'AccountSettings',
  components: {
  },
  data () {
    return {
      errors: {},
      loading: false,
      contact: {phone: '', email: '', allow_text: false},
      totp: {enabled: false},
      new_password: '',
      confirm_password: '',
      user: {},
      qr_url: '',
      qr_image: null,
      display2FASuccess: false,
      displayNewAPIKeyModal: false,
      displayAPIKeyModal: false,
      selectedDomain: null,
      selectedRecord: null,
      api_key: {label: "", prefix: "", is_enabled: true, permissions: {global: {dns: {allow: ''}}}},
      readAllPermissions: [{name: 'None', value: 'none'}, {name: 'Read only', value: 'read'}, {name: 'All', value: 'all'}],
      records_cache: {},
      selectedRecord: {},
    }
  },
  computed: {
    ...mapGetters({ APIKeys: 'StateAPIKeys', Domains: 'StateDomains' }),
  },
  created: function () {
    //console.log(this.Domains)

    this.loading = true

    this.GetAPIKeys(this.$store.state.auth.user.id)
    this.GetDomains({filter_self: true})

    UserService.getUser(this.$store.state.auth.user.id)
      .then(response => {
        if (response.data.attributes) {
          const result = response.data.attributes.find( x => x.key === "contact");
          if (result) {
            this.contact = result["value"]
          }
          const totp_attr = response.data.attributes.find( x => x.key === "totp");
          if (totp_attr) {
            this.totp = totp_attr["value"]
          }
        }    
      })
      .catch(error => {
        this.errors = error.response.data.errors
      })
      .finally(() => this.loading = false)
    
  },
  methods: {
    ...mapActions(['GetAPIKeys', 'CreateAPIKey', 'UpdateAPIKey', 'DeleteAPIKey', 'GetDomains', 'GetDNSRecords']),
    async updateContact () {
      this.loading = true
      this.errors = {}

      UserService.updateContact(this.$store.state.auth.user.id, this.contact)
        .then(response => {
          this.$toast.add({severity:'success', summary: 'Contact info updated', life: 3000});
        })
        .catch(error => {
          this.errors = error.response.data.errors
        })
        .finally(() => this.loading = false)
    },
    async updateNotifications () {
      this.loading = true
      this.errors = {}
    },
    async changePassword () {
      this.loading = true
      this.errors = {}
      
      if (this.new_password.length < 6) {
        this.errors["new_password"] = "Please enter a longer password"
        this.loading = false
        return
      }

      if (this.new_password==this.confirm_password) {
        UserService.changePassword(this.$store.state.auth.user.id, this.new_password)
          .then(response => {
            this.new_password = ''
            this.confirm_password = ''
            this.$toast.add({severity:'success', summary: 'Password updated', life: 3000});
          })
          .catch(error => {
            this.errors = error.response.data.errors
          })
          .finally(() => this.loading = false)  
      }
      else {
        this.errors["new_password"] = "Passwords don't match"
        this.loading = false
        return
      }

    },
    async mfaEnrollRequest () {
      this.errors = {}
      this.$confirm.require({
          message: "Are you sure you want to enroll in multi-factor authentication?  You will need a TOTP compatible application, such as Google Authenticator or Twilio's Authy",
          header: 'Confirmation',
          icon: 'pi pi-exclamation-triangle',
          accept: () => {
              UserService.enrollTOTP(this.$store.state.auth.user.id)

                .then(response => {
                  UserService.getTOTPQRImage(response.data.qr_url)
                    .then(response => {
                      this.qr_image = response
                    })
                    .catch(error => {
                      console.log(error)
                      this.errors = error.response.data.errors
                    })
                    .finally(() => {})   

                  this.display2FASuccess = true
                  this.$toast.add({severity:'success', summary: 'Account enrolled in 2FA', life: 3000});
                  this.totp["enabled"] = true
                })
                .catch(error => {
                  console.log(error)
                  this.errors = error.response.data.errors
                })
                .finally(() => {}) 
          },
          reject: () => {
          }
      });
    },
    async mfaRemove () {
      this.errors = {}
      this.$confirm.require({
          message: "Are you sure you want to remove multi-factor authentication?",
          header: 'Confirmation',
          icon: 'pi pi-exclamation-triangle',
          acceptClass: 'p-button-danger',
          accept: () => {
              UserService.removeTOTP(this.$store.state.auth.user.id)
                .then(response => {
                  this.$toast.add({severity:'success', summary: '2FA removed', life: 3000});
                  this.totp["enabled"] = false
                })
                .catch(error => {
                  this.errors = error.response.data.errors
                })
                .finally(() => {}) 
          },
          reject: () => {
          }
      });
    },
    async generateAPIKey () {
      this.errors = {}
      this.CreateAPIKey(this.api_key)
        .then(response => {
          this.api_key = response.data
          this.displayNewAPIKeyModal = false
          this.displayAPIKeyModal = true
        })
        .catch(error => {
          this.errors = error.response.data.errors
        })
        .finally(() => {})
    },
    openAPIKeyModal(id_) {
      const result = this.APIKeys.keys.find( ({ id }) => id === id_ );
      Object.assign(this.api_key, result)

      if (this.api_key.permissions && this.api_key.permissions.domains) {
        Object.keys(this.api_key.permissions.domains).forEach(domain_id => {
          this.GetDNSRecords(domain_id)
            .then(response => {
              this.records_cache[domain_id] = response.records
              this.selectedRecord[domain_id] = null
            })
          
        })
      }

      this.displayAPIKeyModal = true
    },
    saveAPIKey() {
      this.loading = true
      this.errors = {}
      
      this.UpdateAPIKey(this.api_key)
        .then(response => {
          this.displayAPIKeyModal = false
          this.api_key = {label: "", prefix: "", is_enabled: true}
          this.$toast.add({severity:'success', summary: 'API key updated', detail:response.data.name, life: 3000});
        })
        .catch(error => {
          this.errors = error.response.data.errors
        })
        .finally(() => this.loading = false)
    },
    async removeAPIKey () {
      this.errors = {}
      this.$confirm.require({
          message: "Are you sure you want to remove this API key?",
          header: 'Confirmation',
          icon: 'pi pi-exclamation-triangle',
          acceptClass: 'p-button-danger',
          accept: () => {
              this.DeleteAPIKey(this.api_key)
                .then(response => {
                  this.$toast.add({severity:'success', summary: 'API key removed', life: 3000});
                  this.displayAPIKeyModal = false
                })
                .catch(error => {
                  this.errors = error.response.data.errors
                })
                .finally(() => {}) 
          },
          reject: () => {
          }
      });
    },
    copyAPIKey() {
      navigator.clipboard.writeText(this.api_key.key);
    },
    addDomainToAPIKey() {
      if (!this.api_key.permissions) {
        this.api_key.permissions = {}
      }
      if (!this.api_key.permissions.domains) {
        this.api_key.permissions.domains = {}
      }

      this.api_key.permissions.domains[this.selectedDomain.id] = {allow: "none", name: this.selectedDomain.name, dns: {"allow": "none"}}
      this.GetDNSRecords(this.selectedDomain.id)
        .then(response => {
          this.records_cache[this.selectedDomain.id] = response.records
          this.selectedRecord[this.selectedDomain.id] = null
        })
        .finally(() => {
          this.selectedDomain = null
        })
      //this.selectedDomain = null
    },
    removeDomainFromAPIKey(domain_id) {
      delete this.api_key.permissions.domains[domain_id]
    },

    addRecordToAPIKey(domain_id) {
      if (!this.api_key.permissions.domains[domain_id]) {
        this.api_key.permissions.domains[domain_id] = {}
      }
      if (!this.api_key.permissions.domains[domain_id]["dns"]) {
        this.api_key.permissions.domains[domain_id]["dns"] = {records: {}}
      }
      if (!this.api_key.permissions.domains[domain_id]["dns"]["records"]) {
        this.api_key.permissions.domains[domain_id]["dns"]["records"] = {}
      }  

      this.api_key.permissions.domains[domain_id]["dns"]["records"][this.selectedRecord[domain_id].id] = {allow: "none", name: this.selectedRecord[domain_id].name, type: this.selectedRecord[domain_id].type}

      this.selectedRecord[domain_id] = null
    },
    removeRecordFromAPIKey(domain_id, record_id) {
      delete this.api_key.permissions.domains[domain_id]["dns"]["records"][record_id]
    },
    onCloseAPIKeyModal() {
      //this.GetAPIKeys(this.$store.state.auth.user.id)
      this.api_key = {label: "", prefix: "", is_enabled: true}
    },
    getPermColor(v) {
      if (v == "read") {
        return "success"
      }
      if (v == "all") {
        return "danger"
      }
      return "secondary"
    }
  }
}
</script>

<style lang="scss" scoped>
  .settings-form {
    form {
        margin-top: 2rem;
    }

    .p-field {
        margin-bottom: 1.5rem;
    }
}
</style>